import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '@ups/xplat/core';
import { AuthenticationService } from '@ups/security';

/**
 * Attaches the signed-in user's Auth0 access token as a Bearer to RynBridge calls
 * (Ask Ryn conversations). AskRynService uses a plain HttpClient — it does NOT go
 * through MyHttpClientFactory, deliberately: the factory's JSON casing transform
 * would mangle the PascalCase bridge contract (AskRynMessage.Id/Action). This
 * interceptor adds ONLY the auth header, leaving request/response shape untouched.
 *
 * The bridge validates this token (issuer universalplant.auth0.com, audience
 * security.api.universalplant.com) and scopes the caller to the security userId in
 * its `http://security.universalplant.com/user-id` claim. Without the token the
 * bridge 401s and Ask Ryn shows its "unavailable" banner.
 *
 * Scoped to the bridge host so it never touches non-bridge requests.
 */
@Injectable()
export class AskRynBridgeAuthInterceptor implements HttpInterceptor {
  constructor(private readonly auth: AuthenticationService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const bridgeBase = environment.urls?.bridgeAPI;
    if (!bridgeBase || !req.url.startsWith(bridgeBase)) {
      return next.handle(req);
    }

    return this.auth.getAccessTokenSilently().pipe(
      // If the token can't be acquired, proceed unauthenticated — the bridge 401s
      // and the chat degrades to its banner rather than erroring the whole request.
      catchError(() => of('')),
      switchMap((token) =>
        next.handle(
          token
            ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
            : req
        )
      )
    );
  }
}
